import React, { useEffect, useState } from "react";
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Box, Pagination } from "@mui/material";
import Skeleton from "react-loading-skeleton";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { getQueuedReports } from "../../../redux/actions";
import { AppColor, ModerationReportViewMode } from "../../../types/Constants";
import { QueuedReport } from "../../../types/models/QueuedReport";
import { QueuedReportAllColumnKeys, ReportHistoryAllColumnKeys } from "./QueuedReportListTypes";
import QueueListHeading from "./QueuedReportListHeading";
import QueueListItem from "./QueuedReportListItem";
import QueuedReportDialog from "../components/QueuedReportDialog";

const QueuedReportList: React.FC = () => {

  const dispatch = useAppDispatch();
  const moderationState = useAppSelector(state => state.moderation);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentViewMode, setCurrentViewMode] = useState<string>(ModerationReportViewMode.Queue);
  const [openDetail, setOpenDetail] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] = useState<QueuedReport>();
  const [refreshFlag, setRefreshFlag] = useState<boolean>(false);

  useEffect(() => {
    getReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (refreshFlag) {
      getReportData();
      setRefreshFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshFlag]);

  useEffect(() => {
    if (moderationState.viewMode !== currentViewMode) {
      setCurrentViewMode(moderationState.viewMode);
      if (currentPage === 0) {
        getReportData();
      } else {
        // Call api via currentPage effect
        setCurrentPage(0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moderationState.viewMode]);

  const getReportData = () => {
    setIsLoading(true);
    dispatch(getQueuedReports(
      currentPage,
      moderationState.viewMode === ModerationReportViewMode.History,
      () => setIsLoading(false)));
  };

  const createSkeletonRow = (rowIndex: number) => {

    const column = (currentViewMode === ModerationReportViewMode.Queue) ? QueuedReportAllColumnKeys : ReportHistoryAllColumnKeys;

    return (<TableRow key={rowIndex}>
      {column.map((value, index) => (
        <TableCell key={index}>
          <Box sx={{ pr: 6, maxWidth: "200px" }}>
            <Skeleton
              highlightColor={AppColor.skeletonHighlight}
              baseColor={AppColor.skeletonBase} />
          </Box>
        </TableCell>
      ))}
    </TableRow>);
  };

  const createTableBody = () => {
    if (isLoading) {
      const rows = [];
      for (let i = 0; i < 2; i++) {
        rows.push(createSkeletonRow(i));
      }
      return rows;
    }
    else {
      const reportList = (currentViewMode === ModerationReportViewMode.Queue) ? moderationState.queue?.reports : moderationState.history?.reports;
      return (
        reportList?.map((report, index) => (
          <QueueListItem
            key={index + "_" + report.id}
            report={report}
            viewMode={currentViewMode}
            openDetail={(report) => {
              setSelectedReport(report);
              setOpenDetail(true);
            }}
            setRefreshFlag={setRefreshFlag}
          />
        ))
      );
    }
  };

  return (
    <>
      <Box>
        <TableContainer>
          <Table stickyHeader className="table">
            <TableHead>
              <QueueListHeading viewMode={currentViewMode} />
            </TableHead>
            <TableBody>
              {createTableBody()}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: "flex", justifyContent: "center", m: 2 }}>
          <Pagination
            sx={{
              m: 4,
              "& .MuiPaginationItem-root": {
                color: AppColor.baseOrange,
              },
              "&  .Mui-selected": {
                color: AppColor.baseOrange,
                backgroundColor: AppColor.orangeBackground
              }
            }}
            count={Math.ceil((moderationState.queue?.totalCount ?? 0) / 10)}
            shape="rounded"
            onChange={(event, value) => setCurrentPage(value)}
          />
        </Box>
      </Box>
      {selectedReport &&
        <QueuedReportDialog
          onClose={() => setOpenDetail(false)}
          open={openDetail}
          report={selectedReport}
          setRefreshFlag={setRefreshFlag}
        />
      }
    </>
  );
};

export default QueuedReportList;
